import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, AlertTriangle, Shield, TrendingUp, DollarSign, Clock } from 'lucide-react';

export default function WhyCompositesPage() {
  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="why-composites-page">
      {/* Hero */}
      <section className="py-24 lg:py-32 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">TECHNICAL PRIMER</p>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              WHY COMPOSITES
            </h1>
            <p className="text-stone/60 text-xl leading-relaxed">
              A technical guide to fiber-reinforced polymer reinforcement — understanding when and why to specify FRP over traditional steel.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Corrosion Problem */}
      <section className="py-16 lg:py-24" data-testid="corrosion-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle size={24} className="text-bronze" />
                <p className="text-bronze text-mono text-sm tracking-widest">01 / THE PROBLEM</p>
              </div>
              <h2 className="heading-display text-stone text-4xl mb-6">
                THE CORROSION CRISIS
              </h2>
              <p className="text-stone/70 leading-relaxed mb-6">
                Corrosion costs the United States infrastructure sector <span className="text-bronze font-semibold">$276 billion annually</span>. The primary cause? Steel reinforcement degradation within concrete structures.
              </p>
              <p className="text-stone/70 leading-relaxed mb-6">
                When chloride ions — from seawater, de-icing salts, or industrial processes — penetrate concrete, they attack the passive oxide layer that protects steel rebar. Once this protective layer is compromised, corrosion accelerates exponentially.
              </p>
              <p className="text-stone/70 leading-relaxed">
                The corroding steel expands, creating internal pressures that crack and spall the concrete cover, further accelerating deterioration. What begins as invisible corrosion becomes structural failure.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-surface-dark border border-border-dark p-8"
            >
              <h3 className="heading-section text-stone text-xl mb-6">Corrosion Impact by the Numbers</h3>
              <div className="space-y-6">
                {[
                  { value: '$276B', label: 'Annual U.S. corrosion cost' },
                  { value: '15-25', label: 'Years — typical steel rebar service life in marine environments' },
                  { value: '40%', label: 'Of U.S. bridges rated structurally deficient or functionally obsolete' },
                  { value: '3-5×', label: 'Cost multiplier for repair vs. initial corrosion-resistant design' }
                ].map((stat, index) => (
                  <div key={index} className="flex items-baseline gap-4">
                    <span className="text-bronze text-3xl font-display">{stat.value}</span>
                    <span className="text-stone/60 text-sm">{stat.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The FRP Solution */}
      <section className="py-16 lg:py-24 bg-surface-dark" data-testid="frp-solution-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Shield size={24} className="text-bronze" />
              <p className="text-bronze text-mono text-sm tracking-widest">02 / THE SOLUTION</p>
            </div>
            <h2 className="heading-display text-stone text-4xl mb-6">
              HOW FRP WORKS
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-8 bg-charcoal border border-border-dark"
            >
              <h3 className="heading-section text-stone text-xl mb-4">Material Science</h3>
              <p className="text-stone/70 leading-relaxed text-sm">
                Fiber-reinforced polymer (FRP) composites combine high-strength fibers — basalt, glass, or carbon — with a thermoset resin matrix. The fibers provide tensile strength while the resin transfers load and protects the fibers.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="p-8 bg-charcoal border border-border-dark"
            >
              <h3 className="heading-section text-stone text-xl mb-4">Corrosion Immunity</h3>
              <p className="text-stone/70 leading-relaxed text-sm">
                Unlike steel, FRP contains no ferrous metals. It cannot corrode, rust, or degrade when exposed to chlorides, acids, or alkalis. This fundamental immunity eliminates the primary failure mode of reinforced concrete.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="p-8 bg-charcoal border border-border-dark"
            >
              <h3 className="heading-section text-stone text-xl mb-4">Manufacturing Process</h3>
              <p className="text-stone/70 leading-relaxed text-sm">
                FRP rebar is produced via pultrusion — continuous fibers are pulled through a resin bath and heated die to create a cured, solid bar. The result is a lightweight, high-strength reinforcement with consistent properties.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Basalt vs Fiberglass vs Carbon */}
      <section className="py-16 lg:py-24" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp size={24} className="text-bronze" />
              <p className="text-bronze text-mono text-sm tracking-widest">03 / FIBER COMPARISON</p>
            </div>
            <h2 className="heading-display text-stone text-4xl mb-6">
              BASALT VS FIBERGLASS VS CARBON
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="overflow-x-auto"
          >
            <table className="spec-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Basalt (BFRP)</th>
                  <th>E-Glass (GFRP)</th>
                  <th>Carbon (CFRP)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Tensile Strength</td>
                  <td className="text-bronze">145,000–164,000 psi</td>
                  <td>110,000–160,000 psi</td>
                  <td>200,000+ psi</td>
                </tr>
                <tr>
                  <td>Modulus of Elasticity</td>
                  <td>6.5–10 Msi</td>
                  <td>5.8–8.7 Msi</td>
                  <td>20–24 Msi</td>
                </tr>
                <tr>
                  <td>Cost (relative)</td>
                  <td className="text-bronze">Moderate</td>
                  <td className="text-bronze">Lowest</td>
                  <td>Highest (3-5×)</td>
                </tr>
                <tr>
                  <td>Corrosion Resistance</td>
                  <td>Excellent</td>
                  <td>Excellent</td>
                  <td>Excellent</td>
                </tr>
                <tr>
                  <td>Temperature Resistance</td>
                  <td className="text-bronze">Superior (-50°C to 300°C)</td>
                  <td>Good (-40°C to 250°C)</td>
                  <td>Good</td>
                </tr>
                <tr>
                  <td>Electrical Conductivity</td>
                  <td>Non-conductive</td>
                  <td>Non-conductive</td>
                  <td>Conductive</td>
                </tr>
                <tr>
                  <td>Best Applications</td>
                  <td>Marine, extreme environments</td>
                  <td>General construction</td>
                  <td>High-performance aerospace</td>
                </tr>
              </tbody>
            </table>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 p-6 border-l-4 border-bronze bg-surface-dark"
          >
            <p className="text-stone/70">
              <span className="text-bronze font-semibold">Our recommendation:</span> Basalt fiber (BFRP) represents the cost-performance sweet spot for most infrastructure applications. It delivers superior tensile strength and temperature resistance versus glass fiber, without the premium cost of carbon fiber.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Design Considerations */}
      <section className="py-16 lg:py-24 bg-surface-dark" data-testid="design-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <p className="text-bronze text-mono text-sm tracking-widest">04 / DESIGN CONSIDERATIONS</p>
            </div>
            <h2 className="heading-display text-stone text-4xl mb-6">
              ENGINEERING WITH FRP
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-8 bg-charcoal border border-border-dark"
            >
              <h3 className="heading-section text-stone text-xl mb-4">Deflection-Governed Design</h3>
              <p className="text-stone/70 leading-relaxed text-sm mb-4">
                FRP has a lower modulus of elasticity than steel (6-10 Msi vs 29 Msi). This means FRP-reinforced members are typically <span className="text-bronze">deflection-governed rather than strength-governed</span>.
              </p>
              <p className="text-stone/70 leading-relaxed text-sm">
                Engineers must design for serviceability limits (deflection, crack width) rather than ultimate strength. ACI 440.1R provides detailed guidance for FRP-specific design procedures.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="p-8 bg-charcoal border border-border-dark"
            >
              <h3 className="heading-section text-stone text-xl mb-4">Code Compliance</h3>
              <p className="text-stone/70 leading-relaxed text-sm mb-4">
                FRP reinforcement is covered by established design standards:
              </p>
              <ul className="space-y-2 text-sm text-stone/70">
                <li className="flex items-start gap-2">
                  <span className="text-bronze text-mono">ACI 440.1R-15</span> — Design guide for FRP bars
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-bronze text-mono">ACI 440.11-22</span> — Building code for GFRP
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-bronze text-mono">ASTM D7205</span> — Test method for FRP tensile
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-bronze text-mono">ASTM D7957</span> — Spec for GFRP bars
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lifecycle Cost Analysis */}
      <section className="py-16 lg:py-24" data-testid="lifecycle-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <DollarSign size={24} className="text-bronze" />
              <p className="text-bronze text-mono text-sm tracking-widest">05 / ECONOMICS</p>
            </div>
            <h2 className="heading-display text-stone text-4xl mb-6">
              LIFECYCLE COST ANALYSIS
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-stone/70 leading-relaxed mb-6">
                While FRP reinforcement typically costs more than steel on a per-foot basis, the <span className="text-bronze">total lifecycle cost</span> is often significantly lower for structures in corrosive environments.
              </p>
              <p className="text-stone/70 leading-relaxed mb-6">
                Consider a marine bridge deck: steel rebar may require major rehabilitation at 15-25 years and full replacement at 40-50 years. FRP-reinforced structures can achieve 75-100+ year service life with minimal maintenance.
              </p>
              <p className="text-stone/70 leading-relaxed">
                When maintenance costs, service interruption, and replacement cycles are factored in, FRP delivers substantial lifecycle savings in chloride-exposed applications.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-surface-dark border border-border-dark p-8"
            >
              <h3 className="heading-section text-stone text-xl mb-6">Service Life Comparison</h3>
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-stone">FRP Reinforcement</span>
                    <span className="text-bronze text-mono">100+ years</span>
                  </div>
                  <div className="h-3 bg-charcoal">
                    <div className="h-full bg-bronze w-full" />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-stone">Steel (w/ maintenance)</span>
                    <span className="text-stone/60 text-mono">40-50 years</span>
                  </div>
                  <div className="h-3 bg-charcoal">
                    <div className="h-full bg-stone/40 w-1/2" />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-stone">Steel (marine, unprotected)</span>
                    <span className="text-stone/40 text-mono">15-25 years</span>
                  </div>
                  <div className="h-3 bg-charcoal">
                    <div className="h-full bg-stone/20 w-1/4" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-surface-dark border-t border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-display text-stone text-3xl md:text-4xl mb-6">
              READY TO SPECIFY FRP?
            </h2>
            <p className="text-stone/60 max-w-xl mx-auto mb-8">
              Explore our product specifications or contact our technical team for project-specific guidance.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/products" 
                className="btn btn-outline"
                data-testid="cta-view-products"
              >
                View Products
              </Link>
              <Link 
                to="/contact" 
                className="btn btn-primary"
                data-testid="cta-contact"
              >
                Contact Us
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
